import React, { FC } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import { motion, Variants } from 'framer-motion'
import { LinkButton } from '@/atoms/Button'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { GradientWrapper } from '@/views/TicketsLandingView/NewTicketsLandingView/components/GraidentWrapper'
import { CurrentTheatricalRelease } from '@/views/TicketsLandingView/NewTicketsLandingView/types'

interface Props {
  release: CurrentTheatricalRelease
  className?: string
}

const contentVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

export const ClaimFreeTickets: FC<Props> = ({ release, className }) => {
  const { t } = useTranslate('tickets')

  const { theatricalSlug, angelKeyArt2x3Image, nonTitleArt2x3Image, nonTitleArt16x9Image, title } = release

  return (
    <section className="relative z-10 overflow-hidden">
      <div className={classNames(styles.layoutContainer, 'text-center', className)}>
        {/*mobile*/}
        <div className="absolute inset-0 -z-20 md:hidden">
          <div className="relative h-full w-full">
            <Image
              quality={10}
              sizes="100vw"
              src={nonTitleArt2x3Image}
              alt=""
              fill
              className="object-cover opacity-65 blur-[20px]"
            />
          </div>
        </div>

        {/*desktop*/}
        <div className="absolute inset-0 -z-20 hidden md:block">
          <div className="relative h-full w-full">
            <Image
              quality={10}
              sizes="100vw"
              src={nonTitleArt16x9Image}
              alt=""
              fill
              className="object-cover opacity-65 blur-[40px]"
            />
          </div>
        </div>

        <div className="absolute inset-0 -z-10 block h-full bg-black/65" />

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={contentVariants}
          className="relative z-10"
        >
          <div className="mx-auto flex max-w-[640px] flex-col gap-3 md:gap-6">
            <Text
              weight="bold"
              color="white"
              className="photon-title-md md:photon-heading-lg xl:photon-heading-xl block"
            >
              {t('claimFreeTicketsNext', 'Claim Free Tickets to the Next Angel Theatrical Release.')}
            </Text>

            <Text color="core-gray-400" className="photon-paragraph-sm md:photon-paragraph-xl block">
              {t(
                'claimFreeTicketsProjectNextDescription',
                'Thanks to Angels in our network who have Paid It Forward, we have a limited number of free tickets available to see {{projectName}} in theaters.',
                { projectName: title },
              )}
            </Text>
          </div>

          <div className="relative mt-[72px] grid grid-cols-1 items-center justify-center md:mt-[82px]">
            <TicketImage
              image={angelKeyArt2x3Image}
              className="col-start-1 row-start-1 mx-auto -translate-x-1/4 -rotate-[7deg]"
            />
            <TicketImage
              image={angelKeyArt2x3Image}
              className="col-start-1 row-start-1 mx-auto -translate-y-[20px] translate-x-1/4 rotate-[7deg]"
            />
          </div>

          <div className="ticket" />

          <LinkButton
            size="md"
            className="mx-auto mt-10 md:mt-[48px] md:w-fit"
            href={`/tickets/${theatricalSlug}?promo=claim-free-ticket`}
            variant="white"
          >
            {t('requestFreeTickets', 'Request Free Tickets')}
          </LinkButton>
        </motion.div>
      </div>
    </section>
  )
}

const TicketImage = ({ image, className }: { image: string; className?: string }) => {
  return (
    <GradientWrapper className={classNames('grid h-[224px] w-[149px] rounded-xl', className)}>
      <div className="relative flex flex-col rounded-xl">
        <Image sizes="149px" className="rounded-xl object-cover" src={image} fill alt="" />

        <div
          style={{ background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)' }}
          className="relative z-10 mt-auto grid items-center justify-center rounded-b-xl  pb-6 pt-4"
        >
          <Image src="v1743393008/free-ticket-text_2x_udklxg.webp" width={87} height={44} alt="free tickets" />
        </div>
      </div>
    </GradientWrapper>
  )
}
